import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface AIAnswerBlockProps {
  eyebrow?: string;
  heading: string;
  what: string;
  who: string;
  services?: string;
  benefits?: string;
  why: string;
  highlights?: string[];
}

export default function AIAnswerBlock({
  eyebrow = 'About Lisa Merkle Design',
  heading,
  what,
  who,
  services,
  benefits,
  why,
  highlights = [],
}: AIAnswerBlockProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 100);
        });
        obs.disconnect();
      }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const blocks = [
    { label: 'What We Do', text: what },
    { label: 'Who We Serve', text: who },
    ...(services ? [{ label: 'Core Services', text: services }] : []),
    ...(benefits ? [{ label: 'Key Benefits', text: benefits }] : []),
    { label: 'What Sets Us Apart', text: why },
  ];

  return (
    <section ref={ref} className="bg-white py-24 border-y border-stone/20">
      <div className="max-w-[1400px] mx-auto px-8">

        {/* Header */}
        <div className="reveal grid md:grid-cols-2 gap-8 items-end mb-14">
          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">{eyebrow}</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-charcoal mt-3 leading-tight">
              {heading}
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-4">
            <p className="text-charcoal/50 text-sm leading-relaxed font-sans md:text-right max-w-sm">
              Structured answers for Google, AI search engines, and voice assistants — so you can find us wherever you search.
            </p>
            <Link
              to="/location#contact"
              className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-sans text-stone-dark hover:gap-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Schedule a Consultation <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* Answer Blocks */}
        <div className={`grid ${blocks.length >= 5 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'} gap-0 border border-stone/20`}>
          {blocks.map((block, i) => (
            <div
              key={block.label}
              className={`reveal reveal-delay-${i + 1} p-8 border-stone/15 transition-colors duration-300 hover:bg-cream/60 ${
                i < blocks.length - 1 ? 'border-b lg:border-b-0 lg:border-r' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-px bg-stone" />
                <span className="text-[9px] tracking-[0.25em] uppercase text-stone font-sans whitespace-nowrap">{block.label}</span>
              </div>
              <p className="text-charcoal/70 text-sm leading-relaxed font-sans">{block.text}</p>
            </div>
          ))}
        </div>

        {/* Highlights */}
        {highlights.length > 0 && (
          <div className="reveal mt-8 flex flex-wrap gap-2.5">
            {highlights.map((h, i) => (
              <span
                key={i}
                className="px-4 py-1.5 border border-stone/30 text-charcoal/60 text-[10px] tracking-wider font-sans rounded-full bg-cream/50 whitespace-nowrap"
              >
                {h}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
